// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, {Component, Suspense, lazy} from "react";
import "./App.less";
import "./auth/AuthScreenshotTheme.less";
import {Helmet} from "react-helmet";
import * as Setting from "./Setting";
import {setOrgIsTourVisible, setTourLogo} from "./TourConfig";
import {StyleProvider, legacyLogicalPropertiesTransformer} from "@ant-design/cssinjs";
import {InfoCircleFilled, RobotOutlined} from "@ant-design/icons";
import {Alert, Button, ConfigProvider, Drawer, FloatButton, Layout, Result, Tooltip} from "antd";
import {Route, Switch, withRouter} from "react-router-dom";
import CustomGithubCorner from "./common/CustomGithubCorner";
import * as Conf from "./Conf";
import AiAssistantChat from "./AiAssistantChat";

import * as Auth from "./auth/Auth";
import EntryPage from "./EntryPage";
import * as AuthBackend from "./auth/AuthBackend";
import AuthCallback from "./auth/AuthCallback";
import SamlCallback from "./auth/SamlCallback";
import TelegramLogin from "./auth/TelegramLogin";
import i18next from "i18next";
import {withTranslation} from "react-i18next";
const ManagementPage = lazy(() => import("./ManagementPage"));
const {Footer, Content} = Layout;

import {setTwoToneColor} from "@ant-design/icons";
import * as ApplicationBackend from "./backend/ApplicationBackend";
import * as Cookie from "cookie";
import * as OrganizationBackend from "./backend/OrganizationBackend";
import * as UserBackend from "./backend/UserBackend";

// Ant Design locale imports
import enUS from "antd/locale/en_US";
import zhCN from "antd/locale/zh_CN";
import zhTW from "antd/locale/zh_TW";
import esES from "antd/locale/es_ES";
import frFR from "antd/locale/fr_FR";
import deDE from "antd/locale/de_DE";
import idID from "antd/locale/id_ID";
import jaJP from "antd/locale/ja_JP";
import koKR from "antd/locale/ko_KR";
import ruRU from "antd/locale/ru_RU";
import viVN from "antd/locale/vi_VN";
import ptBR from "antd/locale/pt_BR";
import itIT from "antd/locale/it_IT";
import msMY from "antd/locale/ms_MY";
import trTR from "antd/locale/tr_TR";
import arEG from "antd/locale/ar_EG";
import heIL from "antd/locale/he_IL";
import nlNL from "antd/locale/nl_NL";
import plPL from "antd/locale/pl_PL";
import fiFI from "antd/locale/fi_FI";
import svSE from "antd/locale/sv_SE";
import ukUA from "antd/locale/uk_UA";
import faIR from "antd/locale/fa_IR";
import csCZ from "antd/locale/cs_CZ";
import skSK from "antd/locale/sk_SK";

setTwoToneColor("rgb(87,52,211)");

function getAntdLocale(language) {
  const localeMap = {
    "en": enUS,
    "zh": zhCN,
    "zh-tw": zhTW,
    "es": esES,
    "fr": frFR,
    "de": deDE,
    "id": idID,
    "ja": jaJP,
    "ko": koKR,
    "ru": ruRU,
    "vi": viVN,
    "pt": ptBR,
    "it": itIT,
    "ms": msMY,
    "tr": trTR,
    "ar": arEG,
    "he": heIL,
    "nl": nlNL,
    "pl": plPL,
    "fi": fiFI,
    "sv": svSE,
    "uk": ukUA,
    "fa": faIR,
    "cs": csCZ,
    "sk": skSK,
    "kk": ruRU, // Use Russian for Kazakh as antd doesn't have Kazakh
    "az": trTR, // Use Turkish for Azerbaijani as they're similar
  };
  return localeMap[language] || enUS;
}

class App extends Component {
  constructor(props) {
    super(props);
    this.setThemeAlgorithm();
    let storageThemeAlgorithm = [];
    try {
      storageThemeAlgorithm = localStorage.getItem("themeAlgorithm") ? JSON.parse(localStorage.getItem("themeAlgorithm")) : ["default"];
    } catch {
      storageThemeAlgorithm = ["default"];
    }
    this.state = {
      classes: props,
      selectedMenuKey: 0,
      account: undefined,
      accessToken: undefined,
      uri: null,
      themeAlgorithm: storageThemeAlgorithm,
      themeData: Conf.ThemeDefault,
      logo: this.getLogo(storageThemeAlgorithm),
      requiredEnableMfa: false,
      isAiAssistantOpen: false,
      aiAssistantTriggerPosition: null,
      isAiAssistantTriggerDragging: false,
      application: undefined,
    };
    this.aiAssistantTriggerDrag = {
      isDragging: false,
      hasDragged: false,
      suppressClick: false,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
      width: 0,
      height: 0,
    };
    Setting.initServerUrl();
    Auth.initAuthWithConfig({
      serverUrl: Setting.ServerUrl,
      appName: Conf.DefaultApplication, // the application used in Casdoor root path: "/"
    });
  }

  UNSAFE_componentWillMount() {
    this.updateMenuKey();
    this.getAccount();
    this.getApplication();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const uri = location.pathname;
    if (this.state.uri !== uri) {
      this.updateMenuKey();
    }

    if (this.state.account !== prevState.account) {
      const requiredEnableMfa = Setting.isRequiredEnableMfa(this.state.account, this.state.account?.organization);
      this.setState({
        requiredEnableMfa: requiredEnableMfa,
      });

      if (requiredEnableMfa === true) {
        const mfaType = Setting.getMfaItemsByRules(this.state.account, this.state.account?.organization, [Setting.MfaRuleRequired])
          .find((item) => item.rule === Setting.MfaRuleRequired)?.name;
        if (mfaType !== undefined) {
          this.props.history.push(`/mfa/setup?mfaType=${mfaType}`, {from: "/login"});
        }
      }
    }
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleAiAssistantTriggerResize);
  }

  componentWillUnmount() {
    this.removeAiAssistantTriggerDragListeners();
    window.removeEventListener("resize", this.handleAiAssistantTriggerResize);
  }

  shouldFlattenMenu() {
    const organization = this.state.account?.organization;
    const navItems = Setting.isLocalAdminUser(this.state.account) ? organization?.navItems : (organization?.userNavItems ?? []);

    // If navItems is "all" or not configured, don't flatten
    if (!Array.isArray(navItems) || navItems?.includes("all")) {
      return false;
    }

    // Count how many valid menu items would be visible
    // Filter out any invalid or non-existent menu items
    const validMenuItems = [
      "/", "/shortcuts", "/apps", // Home group
      "/organizations", "/groups", "/users", "/invitations", // User Management
      "/applications", "/providers", "/resources", "/certs", // Identity
      "/roles", "/permissions", "/models", "/adapters", "/enforcers", // Authorization
      "/sessions", "/records", "/tokens", "/verifications", // Logging & Auditing
      "/sysinfo", "/forms", "/syncers", "/webhooks", "/tickets", // Admin
    ];

    const count = navItems.filter(item => validMenuItems.includes(item)).length;
    return count <= Conf.MaxItemsForFlatMenu;
  }

  getSelectedMenuKeyForFlatMenu(uri) {
    // For flattened menu, return the actual child path instead of parent group
    if (uri === "/" || uri.includes("/shortcuts") || uri.includes("/apps")) {
      if (uri === "/") {
        return "/";
      } else if (uri.includes("/shortcuts")) {
        return "/shortcuts";
      } else if (uri.includes("/apps")) {
        return "/apps";
      }
    } else if (uri.includes("/organizations") || uri.includes("/trees") || uri.includes("/groups") || uri.includes("/users") || uri.includes("/invitations")) {
      if (uri.includes("/organizations")) {
        return "/organizations";
      } else if (uri.includes("/groups")) {
        return "/groups";
      } else if (uri.includes("/users")) {
        return "/users";
      } else if (uri.includes("/invitations")) {
        return "/invitations";
      }
    } else if (uri.includes("/applications") || uri.includes("/providers") || uri.includes("/resources") || uri.includes("/certs")) {
      if (uri.includes("/applications")) {
        return "/applications";
      } else if (uri.includes("/providers")) {
        return "/providers";
      } else if (uri.includes("/resources")) {
        return "/resources";
      } else if (uri.includes("/certs")) {
        return "/certs";
      }
    } else if (uri.includes("/roles") || uri.includes("/permissions") || uri.includes("/models") || uri.includes("/adapters") || uri.includes("/enforcers")) {
      if (uri.includes("/roles")) {
        return "/roles";
      } else if (uri.includes("/permissions")) {
        return "/permissions";
      } else if (uri.includes("/models")) {
        return "/models";
      } else if (uri.includes("/adapters")) {
        return "/adapters";
      } else if (uri.includes("/enforcers")) {
        return "/enforcers";
      }
    } else if (uri.includes("/records") || uri.includes("/tokens") || uri.includes("/sessions") || uri.includes("/verifications")) {
      if (uri.includes("/sessions")) {
        return "/sessions";
      } else if (uri.includes("/records")) {
        return "/records";
      } else if (uri.includes("/tokens")) {
        return "/tokens";
      } else if (uri.includes("/verifications")) {
        return "/verifications";
      }
    } else if (uri.includes("/sysinfo") || uri.includes("/forms") || uri.includes("/syncers") || uri.includes("/webhooks") || uri.includes("/tickets")) {
      if (uri.includes("/sysinfo")) {
        return "/sysinfo";
      } else if (uri.includes("/forms")) {
        return "/forms";
      } else if (uri.includes("/syncers")) {
        return "/syncers";
      } else if (uri.includes("/webhooks")) {
        return "/webhooks";
      } else if (uri.includes("/tickets")) {
        return "/tickets";
      }
    } else if (uri.includes("/signup")) {
      return "/signup";
    } else if (uri.includes("/login")) {
      return "/login";
    } else if (uri.includes("/result")) {
      return "/result";
    }
    return -1;
  }

  updateMenuKey() {
    const uri = location.pathname;
    this.setState({
      uri: uri,
    });

    // Check if menu should be flattened and use appropriate key selection
    if (this.shouldFlattenMenu()) {
      const selectedKey = this.getSelectedMenuKeyForFlatMenu(uri);
      this.setState({selectedMenuKey: selectedKey});
      return;
    }

    // Original logic for grouped menu
    if (uri === "/" || uri.includes("/shortcuts") || uri.includes("/apps")) {
      this.setState({selectedMenuKey: "/home"});
    } else if (uri.includes("/organizations") || uri.includes("/trees") || uri.includes("/groups") || uri.includes("/users") || uri.includes("/invitations")) {
      this.setState({selectedMenuKey: "/orgs"});
    } else if (uri.includes("/applications") || uri.includes("/providers") || uri.includes("/resources") || uri.includes("/certs")) {
      this.setState({selectedMenuKey: "/identity"});
    } else if (uri.includes("/roles") || uri.includes("/permissions") || uri.includes("/models") || uri.includes("/adapters") || uri.includes("/enforcers")) {
      this.setState({selectedMenuKey: "/auth"});
    } else if (uri.includes("/records") || uri.includes("/tokens") || uri.includes("/sessions") || uri.includes("/verifications")) {
      this.setState({selectedMenuKey: "/logs"});
    } else if (uri.includes("/sysinfo") || uri.includes("/forms") || uri.includes("/syncers") || uri.includes("/webhooks") || uri.includes("/tickets")) {
      this.setState({selectedMenuKey: "/admin"});
    } else if (uri.includes("/signup")) {
      this.setState({selectedMenuKey: "/signup"});
    } else if (uri.includes("/login")) {
      this.setState({selectedMenuKey: "/login"});
    } else if (uri.includes("/result")) {
      this.setState({selectedMenuKey: "/result"});
    } else {
      this.setState({selectedMenuKey: -1});
    }
  }

  getAccessTokenParam(params) {
    // "/page?access_token=123"
    const accessToken = params.get("access_token");
    return accessToken === null ? "" : `?accessToken=${accessToken}`;
  }

  getCredentialParams(params) {
    // "/page?username=abc&password=123"
    if (params.get("username") === null || params.get("password") === null) {
      return "";
    }
    return `?username=${params.get("username")}&password=${params.get("password")}`;
  }

  getUrlWithoutQuery() {
    return window.location.toString().replace(window.location.search, "");
  }

  getLanguageParam(params) {
    // "/page?language=en"
    const language = params.get("language");
    if (language !== null) {
      Setting.setLanguage(language);
      return `language=${language}`;
    }
    return "";
  }

  getLogo(themes) {
    return Setting.getLogo(themes);
  }

  setThemeAlgorithm() {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    const themeType = url.searchParams.get("theme");
    if (themeType === "dark" || themeType === "default") {
      localStorage.setItem("themeAlgorithm", JSON.stringify([themeType]));
    }
  }

  setLanguage(account) {
    const language = account?.language;
    if (language !== null && language !== "" && language !== i18next.language) {
      Setting.setLanguage(language);
    }
  }

  setTheme = (theme, initThemeAlgorithm) => {
    this.setState({
      themeData: theme,
    });

    if (initThemeAlgorithm) {
      if (localStorage.getItem("themeAlgorithm")) {
        let storageThemeAlgorithm = [];
        try {
          storageThemeAlgorithm = JSON.parse(localStorage.getItem("themeAlgorithm"));
        } catch {
          storageThemeAlgorithm = ["default"];
        }
        this.setState({
          logo: this.getLogo(storageThemeAlgorithm),
          themeAlgorithm: storageThemeAlgorithm,
        });
        return;
      }
      this.setState({
        logo: this.getLogo(Setting.getAlgorithmNames(theme)),
        themeAlgorithm: Setting.getAlgorithmNames(theme),
      });
    }
  };

  getApplication() {
    const applicationName = localStorage.getItem("applicationName");
    if (!applicationName) {
      return;
    }
    ApplicationBackend.getApplication("admin", applicationName)
      .then((res) => {
        if (res.status === "error") {
          Setting.showMessage("error", res.msg);
          return;
        }

        this.setState({
          application: res.data,
        });
      });
  }

  getAccount() {
    const params = new URLSearchParams(this.props.location.search);

    let query = this.getAccessTokenParam(params);
    if (query === "") {
      query = this.getCredentialParams(params);
    }

    const query2 = this.getLanguageParam(params);
    if (query2 !== "") {
      const url = window.location.toString().replace(new RegExp(`[?&]${query2}`), "");
      window.history.replaceState({}, document.title, url);
    }

    if (query !== "") {
      const returnUrl = params.get("returnUrl");

      let newUrl;
      if (returnUrl) {
        const newParams = new URLSearchParams();
        newParams.set("returnUrl", returnUrl);
        newUrl = window.location.pathname + "?" + newParams.toString();
      } else {
        newUrl = this.getUrlWithoutQuery();
      }
      window.history.replaceState({}, document.title, newUrl);
    }

    AuthBackend.getAccount(query)
      .then((res) => {
        let account = null;
        let accessToken = null;
        if (res.status === "ok") {
          account = res.data;
          account.organization = res.data2;
          accessToken = res.data.accessToken;

          if (!localStorage.getItem("language")) {
            this.setLanguage(account);
          }
          this.setTheme(Setting.getThemeData(account.organization), Conf.InitThemeAlgorithm);
          setTourLogo(account.organization.logo);
          setOrgIsTourVisible(account.organization.enableTour);
        } else {
          if (res.data !== "Please login first") {
            Setting.showMessage("error", `${i18next.t("application:Failed to sign in")}: ${res.msg}`);
          }
        }

        this.setState({
          account: account,
          accessToken: accessToken,
        });
      });
  }

  onUpdateAccount(account) {
    this.setState({
      account: account,
    });
  }

  openAiAssistant = () => {
    this.setState({
      isAiAssistantOpen: true,
    });
  };

  getAiAssistantTriggerPoint(event) {
    const source = event.touches?.[0] || event.changedTouches?.[0] || event;
    if (source?.clientX === undefined || source?.clientY === undefined) {
      return null;
    }
    return {
      clientX: source.clientX,
      clientY: source.clientY,
    };
  }

  getClampedAiAssistantTriggerPosition(position, width, height) {
    const margin = 8;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || width + margin * 2;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || height + margin * 2;

    return {
      left: Math.max(margin, Math.min(position.left, viewportWidth - width - margin)),
      top: Math.max(margin, Math.min(position.top, viewportHeight - height - margin)),
    };
  }

  preventAiAssistantDragDefault(event) {
    if (event.cancelable !== false && event.preventDefault) {
      event.preventDefault();
    }
  }

  startAiAssistantTriggerDrag = (event) => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }

    const point = this.getAiAssistantTriggerPoint(event);
    if (!point) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    this.aiAssistantTriggerDrag = {
      ...this.aiAssistantTriggerDrag,
      isDragging: true,
      hasDragged: false,
      startX: point.clientX,
      startY: point.clientY,
      offsetX: point.clientX - rect.left,
      offsetY: point.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };
    this.setState({
      isAiAssistantTriggerDragging: true,
    });
    window.addEventListener("mousemove", this.handleAiAssistantTriggerDragMove);
    window.addEventListener("mouseup", this.handleAiAssistantTriggerDragEnd);
    window.addEventListener("touchmove", this.handleAiAssistantTriggerDragMove, {passive: false});
    window.addEventListener("touchend", this.handleAiAssistantTriggerDragEnd);
    window.addEventListener("touchcancel", this.handleAiAssistantTriggerDragEnd);
    this.preventAiAssistantDragDefault(event);
  };

  handleAiAssistantTriggerDragMove = (event) => {
    const drag = this.aiAssistantTriggerDrag;
    if (!drag.isDragging) {
      return;
    }

    const point = this.getAiAssistantTriggerPoint(event);
    if (!point) {
      return;
    }

    const deltaX = point.clientX - drag.startX;
    const deltaY = point.clientY - drag.startY;
    const dragDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    if (!drag.hasDragged && dragDistance < 4) {
      return;
    }

    drag.hasDragged = true;
    const nextPosition = this.getClampedAiAssistantTriggerPosition({
      left: point.clientX - drag.offsetX,
      top: point.clientY - drag.offsetY,
    }, drag.width, drag.height);

    this.setState({
      aiAssistantTriggerPosition: nextPosition,
    });
    this.preventAiAssistantDragDefault(event);
  };

  handleAiAssistantTriggerDragEnd = () => {
    const drag = this.aiAssistantTriggerDrag;
    if (!drag.isDragging) {
      return;
    }

    this.aiAssistantTriggerDrag = {
      ...drag,
      isDragging: false,
      suppressClick: drag.hasDragged,
    };
    this.setState({
      isAiAssistantTriggerDragging: false,
    });
    this.removeAiAssistantTriggerDragListeners();
  };

  removeAiAssistantTriggerDragListeners() {
    window.removeEventListener("mousemove", this.handleAiAssistantTriggerDragMove);
    window.removeEventListener("mouseup", this.handleAiAssistantTriggerDragEnd);
    window.removeEventListener("touchmove", this.handleAiAssistantTriggerDragMove);
    window.removeEventListener("touchend", this.handleAiAssistantTriggerDragEnd);
    window.removeEventListener("touchcancel", this.handleAiAssistantTriggerDragEnd);
  }

  handleAiAssistantTriggerResize = () => {
    const position = this.state.aiAssistantTriggerPosition;
    const {width, height} = this.aiAssistantTriggerDrag;
    if (!position || !width || !height) {
      return;
    }

    const nextPosition = this.getClampedAiAssistantTriggerPosition(position, width, height);
    if (nextPosition.left !== position.left || nextPosition.top !== position.top) {
      this.setState({
        aiAssistantTriggerPosition: nextPosition,
      });
    }
  };

  handleAiAssistantTriggerClick = (event) => {
    if (this.aiAssistantTriggerDrag.suppressClick) {
      this.aiAssistantTriggerDrag.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.openAiAssistant();
  };

  renderFooter(logo, footerHtml) {
    logo = logo ?? this.state.logo;
    footerHtml = footerHtml ?? this.state.application?.footerHtml;
    return (
      <React.Fragment>
        {!this.state.account ? null : <div style={{display: "none"}} id="CasdoorApplicationName" value={this.state.account.signupApplication} />}
        {!this.state.account ? null : <div style={{display: "none"}} id="CasdoorAccessToken" value={this.state.accessToken} />}
        <Footer id="footer" style={
          {
            textAlign: "center",
          }
        }>
          {
            footerHtml && footerHtml !== "" ?
              <React.Fragment>
                <div dangerouslySetInnerHTML={{__html: footerHtml}} />
              </React.Fragment>
              : (
                Conf.CustomFooter !== null ? Conf.CustomFooter : (
                  <React.Fragment>
                    Powered by KMAuth
                  </React.Fragment>
                )
              )
          }
        </Footer>
      </React.Fragment>
    );
  }

  getAiDraftField(draft, key, fallback = "") {
    const fields = draft?.fields || {};
    const target = draft?.target || {};
    return fields[key] ?? target[key] ?? fallback;
  }

  getAiDraftCurrentRouteParam(paramName) {
    const pathname = this.props.location?.pathname || window.location.pathname || "";
    const patterns = {
      organizationName: /^\/organizations\/([^/]+)$/,
      userOwner: /^\/users\/([^/]+)\/([^/]+)$/,
      userName: /^\/users\/([^/]+)\/([^/]+)$/,
      applicationOrganization: /^\/applications\/([^/]+)\/([^/]+)$/,
      applicationName: /^\/applications\/([^/]+)\/([^/]+)$/,
      providerOwner: /^\/providers\/([^/]+)\/([^/]+)$/,
      providerName: /^\/providers\/([^/]+)\/([^/]+)$/,
    };
    const currentMatch = patterns[paramName]?.exec(pathname);
    if (!currentMatch) {
      return "";
    }

    if (paramName === "userName" || paramName === "applicationName" || paramName === "providerName") {
      return decodeURIComponent(currentMatch[2]);
    }
    return decodeURIComponent(currentMatch[1]);
  }

  getAiDraftContextField(draft, key, fallback = "") {
    if (draft?.entity === "organization" && key === "name") {
      return this.getAiDraftCurrentRouteParam("organizationName") ||
        (Setting.getOrganization() !== "All" ? Setting.getOrganization() : "") ||
        fallback;
    }
    if (draft?.entity === "user") {
      if (key === "owner") {
        return this.getAiDraftCurrentRouteParam("userOwner") || fallback;
      }
      if (key === "name") {
        return this.getAiDraftCurrentRouteParam("userName") || fallback;
      }
    }
    if (draft?.entity === "application") {
      if (key === "organization") {
        return this.getAiDraftCurrentRouteParam("applicationOrganization") || fallback;
      }
      if (key === "name") {
        return this.getAiDraftCurrentRouteParam("applicationName") || fallback;
      }
    }
    if (draft?.entity === "provider") {
      if (key === "owner") {
        return this.getAiDraftCurrentRouteParam("providerOwner") || fallback;
      }
      if (key === "name") {
        return this.getAiDraftCurrentRouteParam("providerName") || fallback;
      }
    }
    return fallback;
  }

  getAiDraftLocatorField(draft, key, fallback = "") {
    const target = draft?.target || {};
    const fields = draft?.fields || {};
    if (draft?.action === "edit") {
      return target[key] ?? this.getAiDraftContextField(draft, key, fallback);
    }
    return fields[key] ?? target[key] ?? fallback;
  }

  getAiDraftRoute(draft) {
    const entity = draft?.entity;
    const owner = this.getAiDraftLocatorField(draft, "owner", "admin");
    const name = this.getAiDraftLocatorField(draft, "name", "");
    const organization = this.getAiDraftLocatorField(draft, "organization", owner);

    if (!name) {
      return "";
    }

    if (entity === "organization") {
      return `/organizations/${encodeURIComponent(name)}`;
    }
    if (entity === "user") {
      return `/users/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`;
    }
    if (entity === "application") {
      return `/applications/${encodeURIComponent(organization)}/${encodeURIComponent(name)}`;
    }
    if (entity === "provider") {
      return `/providers/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`;
    }
    return "";
  }

  pushAiDraftRoute(draft, routeState = {}) {
    const pathname = this.getAiDraftRoute(draft);
    if (!pathname) {
      const entityName = {
        organization: "组织",
        user: "用户",
        application: "应用",
        provider: "Provider",
      }[draft?.entity] || "对象";
      Setting.showMessage("error", `AI 编辑草稿缺少可跳转的目标页面，请让 AI 输出要编辑的${entityName} target`);
      return;
    }

    const mode = routeState.mode ?? (draft.action === "add" ? "add" : "edit");
    this.props.history.push({
      pathname,
      mode,
      state: {
        aiDraft: draft,
        mode,
        ...routeState,
      },
    });

    this.setState({
      isAiAssistantOpen: false,
    });
  }

  buildAiDraftCreatePayload(draft) {
    return {
      ...(draft?.fields || {}),
    };
  }

  handleApplyAiDraft = (draft) => {
    if (!draft || draft.type !== "kmauth.applyDraft") {
      return;
    }

    if (draft.action === "add" && draft.entity === "organization") {
      this.pushAiDraftRoute(draft, {
        aiDraftCreateMode: "local",
      });
      return;
    }

    if (draft.action === "add" && draft.entity === "provider") {
      this.pushAiDraftRoute(draft, {
        aiDraftCreateMode: "local",
      });
      return;
    }

    if (draft.action === "add" && draft.entity === "application") {
      this.pushAiDraftRoute(draft, {
        aiDraftCreateMode: "local",
      });
      return;
    }

    if (draft.action !== "add") {
      this.pushAiDraftRoute(draft);
      return;
    }

    const payload = this.buildAiDraftCreatePayload(draft);
    let request = null;

    if (draft.entity === "organization") {
      request = OrganizationBackend.addOrganization({
        owner: "admin",
        websiteUrl: Setting.getFullServerUrl(),
        logo: `${Setting.StaticBaseUrl}/html/images/1.png`,
        favicon: `${Setting.StaticBaseUrl}/html/images/KMauth.png`,
        passwordType: "bcrypt",
        passwordOptions: ["AtLeast6"],
        passwordObfuscatorType: "Plain",
        countryCodes: ["CN"],
        defaultAvatar: `${Setting.StaticBaseUrl}/img/casbin.svg`,
        tags: [],
        languages: ["zh"],
        initScore: 0,
        orgBalance: 0,
        userBalance: 0,
        balanceCredit: 0,
        enableSoftDeletion: false,
        isProfilePublic: true,
        enableTour: true,
        disableSignin: false,
        mfaRememberInHours: 12,
        balanceCurrency: "CNY",
        accountItems: [
          {name: "Organization", visible: true, viewRule: "Public", modifyRule: "Admin"},
          {name: "ID", visible: true, viewRule: "Public", modifyRule: "Immutable"},
          {name: "Name", visible: true, viewRule: "Public", modifyRule: "Admin"},
          {name: "Display name", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "Avatar", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "User type", visible: true, viewRule: "Public", modifyRule: "Admin"},
          {name: "Password", visible: true, viewRule: "Self", modifyRule: "Self"},
          {name: "Email", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "Phone", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "Country code", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "Country/Region", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "Location", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "Address", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "Affiliation", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "Title", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "ID card type", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "ID card", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "Real name", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "Homepage", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "Bio", visible: true, viewRule: "Public", modifyRule: "Self"},
          {name: "Tag", visible: true, viewRule: "Public", modifyRule: "Admin"},
          {name: "Language", visible: true, viewRule: "Public", modifyRule: "Admin"},
          {name: "Groups", visible: true, viewRule: "Public", modifyRule: "Admin"},
          {name: "Is admin", visible: true, viewRule: "Admin", modifyRule: "Admin"},
          {name: "Is forbidden", visible: true, viewRule: "Admin", modifyRule: "Admin"},
          {name: "Is deleted", visible: true, viewRule: "Admin", modifyRule: "Admin"},
        ],
        ...payload,
      });
    } else if (draft.entity === "user") {
      request = UserBackend.addUser({
        password: "123",
        type: "normal-user",
        ...payload,
      });
    } else if (draft.entity === "application") {
      request = ApplicationBackend.addApplication({
        owner: "admin",
        organization: this.getAiDraftField(draft, "organization", Setting.getRequestOrganization(this.state.account)),
        ...payload,
      });
    }

    if (request === null) {
      Setting.showMessage("error", "暂不支持该 AI 草稿类型");
      return;
    }

    request
      .then((res) => {
        if (res.status === "ok") {
          Setting.showMessage("success", "已创建草稿对象并跳转到编辑页面");
          this.pushAiDraftRoute(draft);
        } else if (this.isAiDraftDuplicateError(res)) {
          Setting.showMessage("info", "目标对象已存在，已跳转到编辑页面并反显 AI 草稿");
          this.pushAiDraftRoute({
            ...draft,
            action: "edit",
          });
        } else {
          Setting.showMessage("error", `AI 草稿创建失败: ${res.msg}`);
        }
      })
      .catch(error => {
        Setting.showMessage("error", `${i18next.t("general:Failed to connect to server")}: ${error}`);
      });
  };

  isAiDraftDuplicateError(res) {
    const message = String(res?.msg || res?.message || "");
    return message.includes("UNIQUE constraint failed") ||
      message.includes("Duplicate entry") ||
      message.includes("already exists") ||
      message.includes("已存在");
  }

  renderAiAssistant() {
    return (
      <Drawer
        title={
          <React.Fragment>
            <Tooltip title="AI 助手">
              <span style={{display: "inline-flex", alignItems: "center"}}>
                <RobotOutlined style={{fontSize: "20px", marginRight: "10px", color: this.state.themeData.colorPrimary}} />
                AI 助手
              </span>
            </Tooltip>
          </React.Fragment>
        }
        className={`kmauth-ai-drawer ${this.state.themeAlgorithm.includes("dark") ? "kmauth-ai-drawer--dark" : ""}`}
        placement="right"
        width={Setting.isMobile() ? "100%" : "min(1280px, calc(100vw - 24px))"}
        mask={false}
        onClose={() => {
          this.setState({
            isAiAssistantOpen: false,
          });
        }}
        open={this.state.isAiAssistantOpen}
        styles={{body: {padding: 0}}}
      >
        <div className="kmauth-ai-drawer-shell">
          <AiAssistantChat
            key={this.state.account?.id || this.state.account?.name || "guest"}
            account={this.state.account}
            onApplyDraft={this.handleApplyAiDraft}
          />
        </div>
      </Drawer>
    );
  }

  renderAiAssistantTrigger() {
    if (!Conf.AiAssistantEnabled || this.state.isAiAssistantOpen) {
      return null;
    }

    const position = this.state.aiAssistantTriggerPosition;
    const triggerStyle = position ? {
      bottom: "auto",
      left: `${position.left}px`,
      right: "auto",
      top: `${position.top}px`,
    } : undefined;

    return (
      <button
        type="button"
        aria-label="AI 助手"
        className={`kmauth-ai-float-trigger ${this.state.themeAlgorithm.includes("dark") ? "kmauth-ai-float-trigger--dark" : ""} ${this.state.isAiAssistantTriggerDragging ? "is-dragging" : ""}`}
        onClick={this.handleAiAssistantTriggerClick}
        onMouseDown={this.startAiAssistantTriggerDrag}
        onTouchStart={this.startAiAssistantTriggerDrag}
        style={triggerStyle}
      >
        <span className="kmauth-ai-float-trigger__icon">
          <RobotOutlined />
        </span>
        {Setting.isMobile() ? null : (
          <span className="kmauth-ai-float-trigger__copy">
            <span className="kmauth-ai-float-trigger__title">AI 助手</span>
          </span>
        )}
      </button>
    );
  }

  isDoorPages() {
    return this.isEntryPages() ||
      window.location.pathname.startsWith("/callback") ||
      window.location.pathname.startsWith("/telegram-login");
  }

  isEntryPages() {
    return window.location.pathname.startsWith("/signup") ||
        window.location.pathname.startsWith("/login") ||
        window.location.pathname.startsWith("/forget") ||
        window.location.pathname.startsWith("/prompt") ||
        window.location.pathname.startsWith("/result") ||
        window.location.pathname.startsWith("/cas") ||
        window.location.pathname.startsWith("/select-plan") ||
        window.location.pathname.startsWith("/buy-plan") ||
        window.location.pathname.startsWith("/qrcode") ||
        window.location.pathname.startsWith("/captcha");
  }

  onClick = ({key}) => {
    if (key !== "/records") {
      if (this.state.requiredEnableMfa) {
        Setting.showMessage("info", "Please enable MFA first!");
      } else {
        this.props.history.push(key);
      }
    }
  };

  onLoginSuccess(redirectUrl) {
    window.google?.accounts?.id?.cancel();
    if (redirectUrl) {
      localStorage.setItem("mfaRedirectUrl", redirectUrl);
    }
    this.getAccount();
  }

  renderPage() {
    if (this.isDoorPages()) {
      let themeData = this.state.themeData;
      let logo = this.state.logo;
      let footerHtml = null;
      if (this.state.organization === undefined) {
        const curCookie = Cookie.parse(document.cookie);
        if (curCookie["organizationTheme"] && curCookie["organizationTheme"] !== "null") {
          themeData = JSON.parse(curCookie["organizationTheme"]);
        }
        if (curCookie["organizationLogo"] && curCookie["organizationLogo"] !== "") {
          logo = curCookie["organizationLogo"];
        }
        if (curCookie["organizationFootHtml"] && curCookie["organizationFootHtml"] !== "") {
          footerHtml = curCookie["organizationFootHtml"];
        }
      }

      return (
        <ConfigProvider
          locale={getAntdLocale(Setting.getLanguage())}
          theme={{
            token: {
              colorPrimary: themeData.colorPrimary,
              borderRadius: themeData.borderRadius,
            },
            algorithm: Setting.getAlgorithm(this.state.themeAlgorithm),
          }}>
          <StyleProvider hashPriority="high" transformers={[legacyLogicalPropertiesTransformer]}>
            <Layout id="parent-area">
              <Content style={{display: "flex", justifyContent: "center"}}>
                {
                  this.isEntryPages() ?
                    <EntryPage
                      account={this.state.account}
                      theme={this.state.themeData}
                      themeAlgorithm={this.state.themeAlgorithm}
                      requiredEnableMfa={this.state.requiredEnableMfa}
                      updateApplication={(application) => {
                        this.setState({
                          application: application,
                        });
                      }}
                      onLoginSuccess={(redirectUrl) => {this.onLoginSuccess(redirectUrl);}}
                      onUpdateAccount={(account) => this.onUpdateAccount(account)}
                      updataThemeData={this.setTheme}
                    /> :
                    <Switch>
                      <Route exact path="/callback" render={(props) => <AuthCallback {...props} {...this.props} application={this.state.application} onLoginSuccess={(redirectUrl) => {this.onLoginSuccess(redirectUrl);}} />} />
                      <Route exact path="/callback/saml" render={(props) => <SamlCallback {...props} {...this.props} application={this.state.application} onLoginSuccess={(redirectUrl) => {this.onLoginSuccess(redirectUrl);}} />} />
                      <Route exact path="/telegram-login" render={(props) => <TelegramLogin {...props} {...this.props} />} />
                      <Route path="" render={() => <Result status="404" title="404 NOT FOUND" subTitle={i18next.t("general:Sorry, the page you visited does not exist.")}
                        extra={<a href="/"><Button type="primary">{i18next.t("general:Back Home")}</Button></a>} />} />
                    </Switch>
                }
              </Content>
              {
                this.renderFooter(logo, footerHtml)
              }
              {
                this.renderAiAssistant()
              }
              {
                this.renderAiAssistantTrigger()
              }
            </Layout>
          </StyleProvider>
        </ConfigProvider>
      );
    }
    return (
      <React.Fragment>
        {/* { */}
        {/*   this.renderBanner() */}
        {/* } */}
        <FloatButton.BackTop />
        <CustomGithubCorner />
        {
          <Suspense fallback={null}>
            <Layout id="parent-area">
              <ManagementPage
                account={this.state.account}
                application={this.state.application}
                uri={this.state.uri}
                themeData={this.state.themeData}
                themeAlgorithm={this.state.themeAlgorithm}
                selectedMenuKey={this.state.selectedMenuKey}
                requiredEnableMfa={this.state.requiredEnableMfa}
                menuVisible={this.state.menuVisible}
                logo={this.state.logo}
                onChangeTheme={this.setTheme}
                onClick = {this.onClick}
                onfinish={() => {
                  this.setState({requiredEnableMfa: false});
                }}
                openAiAssistant={this.openAiAssistant}
                setLogoAndThemeAlgorithm={(nextThemeAlgorithm) => {
                  this.setState({
                    themeAlgorithm: nextThemeAlgorithm,
                    logo: this.getLogo(nextThemeAlgorithm),
                  });
                  localStorage.setItem("themeAlgorithm", JSON.stringify(nextThemeAlgorithm));
                }}
                setLogoutState={() => {
                  this.setState({
                    account: null,
                  });
                }}
              />
              {
                this.renderFooter()
              }
              {
                this.renderAiAssistant()
              }
              {
                this.renderAiAssistantTrigger()
              }
            </Layout>
          </Suspense>
        }
      </React.Fragment>
    );
  }

  renderBanner() {
    if (!Conf.IsDemoMode) {
      return null;
    }

    const language = Setting.getLanguage();
    if (language === "en" || language === "zh") {
      return null;
    }

    return (
      <Alert type="info" banner showIcon={false} closable message={
        <div style={{textAlign: "center"}}>
          <InfoCircleFilled style={{color: "rgb(87,52,211)"}} />
          &nbsp;&nbsp;
          {i18next.t("general:Found some texts still not translated? Please help us translate at")}
          &nbsp;
          <a target="_blank" rel="noreferrer" href={"https://crowdin.com/project/casdoor-site"}>
            Crowdin
          </a>
          &nbsp;!&nbsp;🙏
        </div>
      } />
    );
  }

  render() {
    return (
      <React.Fragment>
        {(this.state.account === undefined || this.state.account === null) ?
          <Helmet>
            <link rel="icon" href={`${Setting.StaticBaseUrl}/html/images/KMauth.png`} />
          </Helmet> :
          <Helmet>
            <title>{Setting.normalizeProductTitle(this.state.account.organization?.displayName)}</title>
            <link rel="icon" href={this.state.account.organization?.favicon} />
          </Helmet>
        }
        <ConfigProvider
          locale={getAntdLocale(Setting.getLanguage())}
          theme={{
            token: {
              colorPrimary: this.state.themeData.colorPrimary,
              colorInfo: this.state.themeData.colorPrimary,
              borderRadius: this.state.themeData.borderRadius,
            },
            algorithm: Setting.getAlgorithm(this.state.themeAlgorithm),
          }}>
          <StyleProvider hashPriority="high" transformers={[legacyLogicalPropertiesTransformer]}>
            {
              this.renderPage()
            }
          </StyleProvider>
        </ConfigProvider>
      </React.Fragment>
    );
  }
}

export default withRouter(withTranslation()(App));
